import { useEffect, useId, useRef, useSyncExternalStore } from "react";
import type { Zeichenname } from "../bausteine/Zeichen";

/* ══════════════════════════════════════════════════════════════════
   Was der Kreis in der Mitte anlegt — und wer es ihm sagt (19.09.2026).

   Luca, nach dem Vergleich mit der Handyfassung von OVIS: *„Der
   Plus-Button kann da ja auch weg. Wir haben dann Plus unten in der
   Bottom-Nav-Bar. Die Optionen müssen dann losleiten."* Und zu den
   Ressourcen: *„‚Maschinen anlegen' ist in dem Fall dann hinter dem
   Plus-Button."*

   ── Was sich damit ändert ─────────────────────────────────────────

   Bis hierher ging der Kreis **blank** auf einen Weg — `/vermieten` für
   die Disposition, `/angebote/neu` für den Vertrieb — und eine Maske, die
   mehr wusste, zeichnete ihren **eigenen** Kreis und ließ den der Leiste
   zurücktreten (die Kundenakte mit ihrem Fächer, 18.09.2026). Das waren
   zwei gelbe Kreise mit zwei Bedienweisen an derselben Stelle: Einer
   sprang sofort, der andere fächerte auf.

   **Jetzt gibt es einen Kreis, und er öffnet ein Blatt** — wie im Vorbild:
   oben, was **diese** Maske anbietet (in der Kundenakte „Vermieten für
   Kranbau", in den Ressourcen „Maschine anlegen"), darunter, was man
   **immer** anlegen kann. Die Maske meldet ihre Griffe an, die Schale
   zeichnet sie. Kein Knopf in der Maske, kein zweiter Kreis.

   ── Was ein Blatt nicht braucht ───────────────────────────────────

   **Gibt es nur einen Griff, gibt es kein Blatt.** Regel 11: *„Im Zweifel
   weniger fragen."* Ein Blatt mit einer Zeile ist eine Rückfrage, deren
   Antwort feststeht — der Kreis tut dann gleich, was die Zeile täte.

   ── Warum eine Anmeldung und keine Tabelle ────────────────────────

   Die Schale kann nicht wissen, welcher Kunde gerade offen ist oder was
   „anlegen" in den Ressourcen heißt, solange der Reiter „Personal" steht.
   Das weiß nur die Maske. Sie ruft `useAnlegegriffe(...)` dort auf, wo sie
   den Zusammenhang hat, und meldet sich beim Verlassen von selbst ab —
   dieselbe Machart wie `useZiehauffrischen` und das Zurücktreten des
   Kreises, das hiermit entfällt.

   **Gelesen wird beim Öffnen, nicht beim Anmelden.** Die Griffe tragen
   Rückrufe, und ein Rückruf aus dem ersten Bild kennt den Zustand des
   ersten Bildes. Die Anmeldung hält deshalb nur einen Zeiger auf die
   jeweils neueste Fassung (`useRef`); das Blatt fragt sie ab, wenn es
   aufgeht. So muss keine Maske ihre Liste in `useMemo` einpacken, damit
   die Schale nicht bei jedem Tastendruck neu zeichnet.

   **Mehrere Anmeldungen: die jüngste zuerst.** Liegt über einer Maske ein
   Blatt, das selbst anlegen kann, steht dessen Gruppe oben — sie ist das,
   was man gerade vor Augen hat.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Griff im Blatt hinter dem Kreis. */
export type Anlegegriff = {
  /** i18n-Schlüssel des Worts — Sache, dann Tätigkeit: „Maschine anlegen". */
  wort: string;
  /** Platzhalter für `wort`, etwa `{ kunde: "Kranbau" }`. */
  werte?: Record<string, string | number>;
  /** i18n-Schlüssel des leisen Satzes darunter. Freiwillig. */
  satz?: string;
  zeichen: Zeichenname;
  /** Was er tut. Das Blatt ist dann schon zu. */
  tun: () => void;
  /**
   * Ersetzt diesen allgemeinen Griff (`Anlegeblatt.tsx`) — „Vermieten für
   * Kranbau" steht dann statt des blanken „Vermieten" und nicht daneben.
   */
  statt?: "vermieten" | "angebot" | "kunde";
  /**
   * i18n-Schlüssel: warum der Griff gerade nicht geht (07.10.2026, Paket 9).
   * Er steht dann gesperrt da und sagt es — ein Knopf ist nie stumm gesperrt
   * (docs/64, Regel 9). Bisher nur im Menü „Aktionen" am Schreibtisch.
   */
  gesperrt?: string | undefined;
  /** Ein Griff, der etwas beendet (Stornieren, Verwerfen) — in Stoppfarbe. */
  gefahr?: boolean | undefined;
};

/** Was eine Maske anmeldet: eine Überschrift und ihre Griffe. */
export type Anlegegruppe = {
  /** Schon übersetzt — oft ein Name („Kranbau Ostfriesland GmbH"). */
  titel: string;
  griffe: Anlegegriff[];
  /**
   * Was der runde Knopf dann zeigt (20.09.2026). Luca: *„Diesen
   * Action-Button machst du einfach immer dann, wenn es Optionen gibt, die
   * wir hier wählen können."*
   *
   * | | Zeichen | wofür |
   * |---|---|---|
   * | `"handlung"` (Vorgabe) | Blitz | was an dieser Sache dran ist: senden, übernehmen, nachtragen |
   * | `"neu"` | Plus | hier entsteht etwas Neues — „Maschine anlegen" im Stamm |
   *
   * Melden mehrere Masken zugleich an, entscheidet **eine** Handlung: Wo
   * gehandelt **und** angelegt werden kann, ist der Knopf ein Blitz.
   *
   * **Am Schreibtisch dieselbe Teilung** (07.10.2026, Paket 9): `"neu"`
   * steht unter „Neu" im Kopf, `"handlung"` unter „Aktionen" daneben
   * (`schale/Kopfwerkzeuge.tsx`).
   */
  art?: "neu" | "handlung";
};

type Eintrag = { id: string; lies: () => Anlegegruppe | null };

let eintraege: Eintrag[] = [];
let stand = 0;
const horcher = new Set<() => void>();

function melden(): void {
  stand += 1;
  for (const h of horcher) h();
}

function abonnieren(h: () => void): () => void {
  horcher.add(h);
  return () => { horcher.delete(h); };
}

/**
 * „Hier legt man das an" — solange die Maske steht, stehen ihre Griffe
 * oben im Blatt hinter dem Kreis.
 *
 * `null` oder eine leere Liste heißt: gerade nichts. Die Maske darf ihre
 * Griffe von Bild zu Bild ändern (Reiter wechselt, Recht fehlt); gelesen
 * wird beim Öffnen des Blatts.
 */
export function useAnlegegriffe(gruppe: Anlegegruppe | null): void {
  const id = useId();
  const zeiger = useRef(gruppe);
  zeiger.current = gruppe;
  /* Ob überhaupt etwas angemeldet ist, muss die Leiste **vor** dem Öffnen
     wissen — sonst kann sie nicht entscheiden, ob der Kreis gleich springt.
     Gemeldet wird deshalb, wenn die Zahl der Griffe wechselt, nicht bei
     jedem Bild. */
  const zahl = gruppe?.griffe.length ?? 0;
  useEffect(() => {
    eintraege = [...eintraege, { id, lies: () => zeiger.current }];
    melden();
    return () => {
      eintraege = eintraege.filter((e) => e.id !== id);
      melden();
    };
  }, [id]);
  useEffect(() => { melden(); }, [zahl]);
}

/** Die angemeldeten Gruppen, jüngste zuerst, ohne leere. */
export function angemeldeteGruppen(): Anlegegruppe[] {
  return [...eintraege]
    .reverse()
    .map((e) => e.lies())
    .filter((g): g is Anlegegruppe => !!g && g.griffe.length > 0);
}

/** Zeichnet neu, sobald sich eine Anmeldung ändert. */
export function useAnlegestand(): number {
  return useSyncExternalStore(abonnieren, () => stand, () => 0);
}

/* ── Das Blatt: offen oder zu ─────────────────────────────────────── */

let offen = false;
const blattHorcher = new Set<() => void>();

function blattMelden(): void {
  for (const h of blattHorcher) h();
}

export function anlegenOeffnen(): void {
  if (offen) return;
  offen = true;
  blattMelden();
}

export function anlegenSchliessen(): void {
  if (!offen) return;
  offen = false;
  blattMelden();
}

export function useAnlegenOffen(): boolean {
  return useSyncExternalStore(
    (h) => { blattHorcher.add(h); return () => { blattHorcher.delete(h); }; },
    () => offen,
    () => false,
  );
}
